import React from "react";
// also exported from '@storybook/react' if you can deal with breaking changes in 6.1
import styled from "styled-components";
import { Typography } from "@mui/material";

import { InfoIcon } from "../../CommonUI/Icon/InfoIcon";
import { TextIcon } from "../../CommonUI/TextIcon";
import { FormBuilder } from "../FormBuilder";
import { FormConfiguration } from "../FormConfiguration";
import { getDataDefinitionForRulePage, getFormConfig } from "../Testing/FormTestData";
import { IAction } from "../../CommonUI/IAction";
import { FormRule } from "../Structure/FormRule";

import { getLogicExpressionResultElseIfAtEnd } from "../../Expressions/Testing/LogicData";
import { getDivideSimpleRule } from "../../Expressions/Testing/MathData";
import { StoryNode } from "../../CommonUI/StoryBook/StoryData";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

const Wrapper = styled("div")<{
    width?: number;
    height?: number;
    backgroundColor?: string;
}>`
    flex: 1;
    display: flex;
    flex-flow: column;
    background: ${(props) => props.backgroundColor || "transparent"};
`;

class TopExtensionPanel implements IAction {
    name = "Info";
    icon = (<InfoIcon />);

    renderUI() {
        return (
            <Wrapper backgroundColor={UIStyleConstants.GRAY_COLOR}>
                <div>
                    <Typography>Information</Typography>
                </div>
            </Wrapper>
        );
    }
}

class BottomExtensionPanel implements IAction {
    name = "Doc";
    icon = (<TextIcon name="D" />);

    renderUI() {
        return (
            <Wrapper backgroundColor="green">
                <div>
                    <Typography>Documentation</Typography>
                </div>
            </Wrapper>
        );
    }
}

export const FormDesignerStories = [
    new StoryNode(
        () => {
            let ui = getFormConfig();
            return <FormBuilder form={ui}></FormBuilder>;
        },

        "Form Builder",
        "All Controls",
    ),

    new StoryNode(
        () => {
            let ui = new FormConfiguration();
            return <FormBuilder form={ui}></FormBuilder>;
        },

        "Form Builder",
        "No Controls",
    ),

    new StoryNode(
        () => {
            let ui = new FormConfiguration();
            return (
                <FormBuilder
                    form={ui}
                    formName="Test Form"
                ></FormBuilder>
            );
        },

        "Form Builder",
        "Form Title",
    ),

    new StoryNode(
        () => {
            let ui = new FormConfiguration();
            return (
                <FormBuilder
                    form={ui}
                    formName="Test Form"
                    onFormSave={() => {}}
                ></FormBuilder>
            );
        },

        "Form Builder",
        "Form Save",
    ),

    new StoryNode(
        () => {
            let ui = getFormConfig();
            ui.doNotScrollLastContainerOnForm = true;
            ui.doNotScrollFirstContainerOnForm = true;
            return (
                <FormBuilder
                    form={ui}
                    formName="Test Form"
                ></FormBuilder>
            );
        },

        "Form Builder",
        "Pinned Sections",
    ),

    new StoryNode(
        () => {
            let ui = getFormConfig();
            return (
                <FormBuilder
                    form={ui}
                    onFormClose={() => {}}
                ></FormBuilder>
            );
        },

        "Form Builder",
        "Close Action",
    ),

    new StoryNode(
        () => {
            let ui = getFormConfig();
            ui.gapBetweenContainers = 80;
            return (
                <FormBuilder
                    form={ui}
                    formName="Test Form"
                ></FormBuilder>
            );
        },

        "Form Builder",
        "Space Between Containers",
    ),

    new StoryNode(
        () => {
            let ui = getFormConfig();
            ui.showLinesBetweenContainers = true;
            return (
                <FormBuilder
                    form={ui}
                    formName="Test Form"
                ></FormBuilder>
            );
        },

        "Form Builder",
        "Lines Between Containers",
    ),

    new StoryNode(
        () => {
            let ui = getFormConfig();
            ui.formMargin = 20;
            ui.defaultInnerContainerMargin = 40;
            ui.defaultShowContainerBorder = true;
            return (
                <FormBuilder
                    form={ui}
                    formName="Test Form"
                ></FormBuilder>
            );
        },

        "Form Builder",
        "Form Margin and Default Container Margin",
    ),

    new StoryNode(
        () => {
            let ui = getFormConfig();
            ui.formBackgroundColor = UIStyleConstants.YELLOW_COLOR;
            return (
                <FormBuilder
                    form={ui}
                    formName="Test Form"
                ></FormBuilder>
            );
        },

        "Form Builder",
        "Form Background Color",
    ),

    new StoryNode(
        () => {
            let ui = getFormConfig();
            ui.defaultContainerBackgroundColor = UIStyleConstants.YELLOW_COLOR;
            return (
                <FormBuilder
                    form={ui}
                    formName="Test Form"
                ></FormBuilder>
            );
        },

        "Form Builder",
        "Container Background Color",
    ),

    new StoryNode(
        () => {
            let ui = getFormConfig();
            ui.defaultContainerBorderColor = UIStyleConstants.YELLOW_COLOR;
            ui.defaultShowContainerBorder = true;
            return (
                <FormBuilder
                    form={ui}
                    formName="Test Form"
                ></FormBuilder>
            );
        },

        "Form Builder",
        "Container Default Border",
    ),

    new StoryNode(
        () => {
            let ui = getFormConfig();
            let topDesignerExtensions: IAction[] = [new TopExtensionPanel()];
            let bottomDesignerExtensions: IAction[] = [new BottomExtensionPanel()];
            return (
                <FormBuilder
                    form={ui}
                    topDesignerExtensions={topDesignerExtensions}
                    bottomDesignerExtensions={bottomDesignerExtensions}
                    formName="Test Form"
                ></FormBuilder>
            );
        },

        "Form Builder",
        "Form UI Extensions",
    ),

    new StoryNode(
        () => {
            let ui = getFormConfig();
            let datadef = getDataDefinitionForRulePage();
            ui.inputData = datadef;

            let expression1 = getLogicExpressionResultElseIfAtEnd();
            let rule1 = new FormRule(expression1, ["isEmployee", "income"]);

            let expression2 = getDivideSimpleRule();
            let rule2 = new FormRule(expression2, []);

            ui.rules = [rule1, rule2];
            return (
                <FormBuilder
                    form={ui}
                    formName="Test Form"
                ></FormBuilder>
            );
        },

        "Form Builder",
        "With Rules",
    ),
];
